import type { JSX, ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { formatPhone } from '@/lib/phone';
import { fieldLabel, formError } from '../shell/look';
import { PhoneInput } from './phone-input';
import { LoginRow } from './telegram-login-parts';
import {
  EMPTY_ACCOUNT,
  accountSchema,
  firstLoginError,
  secretSchema,
} from './telegram-login-schema';
import type { AccountForm, SecretForm } from './telegram-login-schema';

interface StepProps<T> {
  readonly onSubmit: (values: T) => Promise<void>;
  /** What Telegram or the API said last time, if it refused. */
  readonly refused: string | undefined;
  readonly footer: (busy: boolean) => ReactNode;
}

/** Step one: the account's app credentials and the phone the code goes to. */
export function AccountStep({ onSubmit, refused, footer }: StepProps<AccountForm>): JSX.Element {
  const form = useForm<AccountForm>({
    resolver: zodResolver(accountSchema),
    defaultValues: EMPTY_ACCOUNT,
  });
  const error = firstLoginError(form.formState.errors) ?? refused;
  const text = (name: 'name' | 'apiId' | 'apiHash') => ({
    value: form.watch(name),
    onChange: (v: string) => form.setValue(name, v, { shouldValidate: form.formState.isSubmitted }),
  });
  return (
    <form
      noValidate
      className="flex flex-1 flex-col gap-3.5"
      onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}
    >
      <LoginRow
        id="tg-name"
        label="Display name"
        help="Also the source name; it becomes part of the source id."
        {...text('name')}
      />
      <LoginRow id="tg-api-id" label="API ID" mono {...text('apiId')} />
      <LoginRow
        id="tg-api-hash"
        label="API hash"
        mono
        secret
        help="From my.telegram.org. Never shown again after saving."
        {...text('apiHash')}
      />
      <div className={fieldLabel}>
        <label htmlFor="tg-phone">Phone number</label>
        <Controller
          control={form.control}
          name="phone"
          render={({ field }) => (
            <PhoneInput id="tg-phone" value={field.value} onChange={field.onChange} />
          )}
        />
      </div>
      <ErrorLine error={error} />
      {footer(form.formState.isSubmitting)}
    </form>
  );
}

/** Step two is the code Telegram sent; step three, the 2FA password if the account has one. */
export function SecretStep({
  step,
  phone,
  onSubmit,
  refused,
  footer,
}: StepProps<SecretForm> & { step: 2 | 3; phone: string }): JSX.Element {
  const form = useForm<SecretForm>({
    resolver: zodResolver(secretSchema[step]),
    defaultValues: { secret: '' },
  });
  const error = firstLoginError(form.formState.errors) ?? refused;
  const props = {
    value: form.watch('secret'),
    onChange: (v: string) =>
      form.setValue('secret', v, { shouldValidate: form.formState.isSubmitted }),
  };
  return (
    <form
      noValidate
      className="flex flex-1 flex-col gap-3.5"
      onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}
    >
      {step === 2 ? (
        <LoginRow
          id="tg-code"
          label="Login code"
          mono
          help={`Sent to ${formatPhone(phone)} by Telegram.`}
          {...props}
        />
      ) : (
        <LoginRow
          id="tg-password"
          label="Two-factor password"
          secret
          help="Only asked for when the account has 2FA enabled."
          {...props}
        />
      )}
      <ErrorLine error={error} />
      {footer(form.formState.isSubmitting)}
    </form>
  );
}

function ErrorLine({ error }: { error: string | undefined }): JSX.Element | null {
  if (error === undefined) return null;
  return (
    <div role="alert" className={formError}>
      {error}
    </div>
  );
}
